import parse from "html-react-parser";
import { useCallback, useMemo } from "preact/compat";
import React, { useEffect, useState } from 'react';
import ReactPlayer from "react-player/lazy";
import Slider from "react-slick";

const sliderDefaultSettings = {
  dots: true,
  dotsClass: 'slick-dots',
  speed: 2000,
  waitForAnimate: false,
  swipeToSlide: true,
  autoPlay: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  initialSlide: 0,
  fade: false,
  lazyLoad: "progressive"
}

const SlideshowSlick = ({ slides, onEnd, areaId, delay, logo, mode, lazyLoad, isHidden, isFirst, isUniq, infinite }) => {
  const [currentSlider, setCurrentSlider] = useState(0);
  const slick = React.useRef(null);
  const timeoutRef = React.useRef(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const isMTKFireTV = /AFT/.test(navigator.userAgent);

  const sliderSettings = useMemo(() => ({ ...sliderDefaultSettings, fade: true, lazyLoad }), [lazyLoad]);
  const containerWidth = useMemo(() => slick?.current?.base?.clientWidth, [slick]);
  const containerHeight = useMemo(() => slick?.current?.base?.clientHeight, [slick]);

  useEffect(() => {
    if (slick?.current) {
      slick.current.slickGoTo(0);
    }

    if (!slides.length) {
      onEnd();
    }
  }, [slides, isHidden]);

  const nextSlide = useCallback((next) => {
    if (next >= (slides.length - 1) && !isUniq) {
      onEnd();
    } else if (slick?.current?.slickNext != null) {
      try {
        slick?.current?.slickNext();
      } catch (_) {
        // console.warn(error);
      }
    }
  }, [onEnd, slick, slides, isUniq]);

  const resetTimeout = useCallback((next, needTimeout = true) => {
    if (!isHidden) {
      setCurrentSlider(next);

      if (mode === "html" && !slides[next]?.includes('__videoplayer__')) {
        clearTimeout(timeoutRef.current);

        timeoutRef.current = setTimeout(() => {
          nextSlide(next)
        }, delay);
      } else if (slides[next]?.url && !slides[next]?.url.endsWith('mp4')) {
        if (needTimeout) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = setTimeout(() => {
            nextSlide(next)
          }, delay / slides.length);
        } else {
          nextSlide(next);
        }
      }
    }
  }, [nextSlide, slides, isHidden]);

  const options = useMemo(() => ({
    replace: ({ attribs }) => {
      const [playing, setPlaying] = useState(true);
      const [duration, setDuration] = useState(0);

      if (!attribs) {
        return;
      }

      if (typeof attribs.onerror === 'string') {
        attribs.onerror = (event) => {
          const img = event.target;
          img.style.display = 'none';
        };
      }

      if (attribs["data-image"]) {
        if (attribs["data-imagetype"] === 'mandatory' && window?.blacklist?.includes(attribs["data-image"])) {
          nextSlide(currentSlider);
          // addEvent('blackListFrame', area.slug, {currentFrame, isNext: isHidden});
          return;
        }

        return (
          <img
            {...attribs}
            src={attribs["data-image"]}
            alt='slide'
            onError={(event) => {
              if (attribs["data-imagetype"] === 'recommended') {
                const parent = event.target.parentNode.parentNode;
                const img = parent.querySelector('.default-img');

                if (event.target.src !== logo) {
                  event.target.src = logo;
                  img.style.display = 'block';
                } else {
                  img.style.display = 'none';
                }
              } else if (attribs["data-imagetype"] === 'mandatory') {
                nextSlide(currentSlider);
                // addEvent('blackListFrame', area.slug, {currentFrame, isNext: isHidden});
              } else if (attribs["data-imagetype"] === 'remove') {
                const parent = event.target.parentNode.parentNode;
                const img = parent.querySelector('.logo-container');
                img.style.display = 'none';
              }
            }}
          />
        )
      }

      if (attribs.class === '__videoplayer__' && attribs["data-video"]) {
        return (<div style={{ backgroundColor: '#000', position: 'absolute', top: 0, left: 0, zIndex: 2, width: '100%', height: '100%' }}>
          <div style={{ opacity: isHidden ? 0 : 1, transition: 'opacity 500ms ease-out' }}>
            <ReactPlayer
              id={'test-video-id'}
              url={attribs["data-video"]}
              muted={true}
              key={`video_${isHidden}${playing}`}
              controls={false}
              volume={0}
              playing={!isHidden && playing}
              width={"100vw"}
              height={"100vh"}
              onReady={() => {
                setPlaying(true)
              }}
              onProgress={(v) => {
                if (v.playedSeconds > (duration - 2)) {
                  nextSlide(currentSlider);
                }
              }}
              onDuration={(t) => setDuration(t)}
              config={{
                youtube: {
                  playerVars: { showinfo: 0, rel: 0, fs: 0, modestbranding: 1 }
                },
                vimeo: {
                  playerOptions: { portrait: false, responsive: true, transparent: false }
                },
                dailymotion: {
                  params: { enable_autonext: false, enable_info: false, enable_playback_controls: false }
                },
                file: {
                  hlsOptions: isMTKFireTV
                    ? {
                        capLevelToPlayerSize: true,
                        startLevel: 2,
                        autoStartLoad: true,
                        abrEwmaDefaultEstimate: 500000,
                      }
                    : {},
                  dashOptions: isMTKFireTV
                    ? {
                        streaming: {
                          abr: {
                            autoSwitchBitrate: { video: false },
                            initialBitrate: { video: 1500 },
                          },
                        },
                      }
                    : {},
                },
              }}

            />
          </div>
        </div>);
      }
    }
  }), [nextSlide, isHidden]);

  const renderSlide = useCallback((slide, index) => {
    let html = '';

    if (!slide) {
      console.log('empty slide');
    }

    const { url = '', thumbnail = '' } = slide ?? {};

    if (mode === "html") {
      html = slide
    } else if (url?.endsWith('mp4')) {
      html = (
        <>
          <div style={{ backgroundImage: `url('${thumbnail}')`, backgroundSize: "cover", backgroundPosition: 'center center', height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 2, filter: 'blur(10px)', backgroundColor: 'rgba(0,0,0,0.9)' }} />
          <div style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, zIndex: 7, backgroundColor: 'rgba(0,0,0,0.5)' }} />
          {!isHidden ? <video
            onLoadedData={() => setIsVideoLoaded(true)}
            src={`${url}#t=1`}
            controls={false}
            autoPlay={true}
            muted={true}
            preload={"auto"}
            onEnded={() => {
              setIsVideoLoaded(false);
              onEnd();
            }}
            style={{ transition: 'opacity 1s', opacity: isHidden || !isVideoLoaded ? 0 : 1, position: 'relative', zIndex: isVideoLoaded ? 10 : 5 }}
          /> : null}
        </>
      )
    } else {
      html = (
        <div className={"picture"} style={{ backgroundImage: `url('${url}')`, backgroundColor: '#444' }} />
      )
    }

    return (<div className={`frame ${!isHidden && mode === 'html' ? 'has-animations' : ''} ${isFirst ? 'initial-slide' : ''}`} key={`frame_${index}`}>
      {mode === 'html' ? parse(html ?? "", options) : html}
    </div>)
  }, [isVideoLoaded, options, mode, isFirst, isHidden]);

  const handleBeforeChange = useCallback((cur, next, a) => {
    if (!isHidden && !slides[0]?.url?.endsWith('mp4')) {
      resetTimeout(next);
    }

    if (!isHidden) {
      const activeSlide = document.getElementsByClassName('append_frame')[0];
      const scrollContainers = activeSlide?.getElementsByClassName('scrollContainer')[0];
      const activeText = scrollContainers?.getElementsByClassName('scrollText')[0];
      const height = activeText?.offsetHeight;
      const heightContainer = scrollContainers?.offsetHeight;

      if (heightContainer - height < 0 && activeText.style) {
        activeText.style.transform = `translate(0, ${heightContainer - height}px)`;
        activeText.style.transition = `transform ${delay - 2000}ms 1s linear`;
      }
    }
  }, [slides, resetTimeout, isHidden]);

  const handleRefChange = useCallback((slider) => {
    if (slider) {
      slick.current = slider;
    }
  }, [slick]);

  return (
    <div style={{ 'overflow': 'hidden' }}>
      <Slider
        key={areaId}
        style={{ 'overflow': 'hidden' }}
        {...sliderSettings}
        dots={true}
        vertical={(containerWidth && containerHeight && containerHeight > containerWidth)}
        adaptiveHeight={false}
        adaptiveWidth={false}
        centerMode={false}
        infinite={infinite}
        ref={handleRefChange}
        beforeChange={handleBeforeChange}
      // afterChange={(cur) => {}}
      >
        {slides?.map((url, i) => renderSlide(url, i))}
      </Slider>
    </div>
  )
}

SlideshowSlick.defaultProps = {
  delay: 5000,
  mode: 'slide',
  infinite: false,
  onEvent: () => { }
}

export default React.memo(SlideshowSlick, (props, nextProps) => {
  return props.slides[0] === nextProps.slides[0] && props.isHidden === nextProps.isHidden
})
